import { useEffect, useState } from "react";
import axiosInstance from "../config/axiosInstance";
import { useCompany } from "../hooks/useCompany";
import EmptyState from "../components/EmptyState";
import NoData from "../components/NoData";
import { Link, useNavigate } from "react-router-dom";
import { FiBox, FiEdit2, FiTrash2 } from "react-icons/fi";
import { extractList } from "../utils/api";
import Pagination from "../components/Pagination";
import { formatDate } from "../utils/format";

const ProductsPage = () => {
  const navigate = useNavigate();
  const { company } = useCompany();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("TODAS");
  const [urgencyFilter, setUrgencyFilter] = useState("TODOS");
  const [activeProductId, setActiveProductId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 20;

  const verificationStatus = company?.statusVerificacao;
  const canCreateProduct = verificationStatus === "APROVADO";
  const isVerificationBlocked = ["PENDENTE", "REJEITADO"].includes(
    verificationStatus,
  );

  const fetchProducts = async (page = 1) => {
    if (!company?.id) return;
    setLoading(true);
    try {
      const response = await axiosInstance.get("/products", {
        params: { itemsPerPage, page, companyId: company.id },
      });
      setProducts(extractList(response));
      setTotalPages(response.data?.data?.totalPages || 1);
    } catch (error) {
      setProducts([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(currentPage);
  }, [company?.id, currentPage]);

  const resolveMediaSrc = (mediaItem) => {
    if (!mediaItem || typeof mediaItem !== "string") return "";

    if (
      mediaItem.startsWith("data:") ||
      mediaItem.startsWith("blob:") ||
      mediaItem.startsWith("http://") ||
      mediaItem.startsWith("https://")
    ) {
      return mediaItem;
    }

    return `https://relinq.s3.sa-east-1.amazonaws.com/${mediaItem}`;
  };

  const getProductPreviewSrc = (product) => {
    if (Array.isArray(product?.mediaUrls) && product.mediaUrls[0]) {
      return product.mediaUrls[0];
    }

    if (Array.isArray(product?.media) && product.media[0]) {
      return resolveMediaSrc(product.media[0]);
    }

    return "";
  };

  const handleEditProduct = (productId) => {
    navigate(`/produtos/${productId}/editar`);
  };

  const handleDeleteProduct = async (productId) => {
    const confirmed = window.confirm(
      "Deseja realmente excluir este produto? Esta ação não pode ser desfeita.",
    );

    if (!confirmed) return;

    setActiveProductId(productId);

    try {
      await axiosInstance.delete(`/products/${productId}`);
      setProducts((currentProducts) =>
        currentProducts.filter((product) => product.id !== productId),
      );
    } catch (error) {
      window.alert("Não foi possível excluir o produto. Tente novamente.");
    } finally {
      setActiveProductId(null);
    }
  };

  const categories = Array.from(
    new Set(
      products
        .map(
          (product) =>
            product?.Category?.name ||
            product?.category?.name ||
            product?.categoryName,
        )
        .filter(Boolean),
    ),
  ).sort((a, b) => a.localeCompare(b));

  const filteredProducts = products.filter((product) => {
    const title = product?.title || "";
    const categoryName =
      product?.Category?.name ||
      product?.category?.name ||
      product?.categoryName ||
      "";
    const condition = product?.priceCondition || "";

    const matchesSearch =
      !searchTerm ||
      [title, categoryName, condition]
        .join(" ")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesCategory =
      categoryFilter === "TODAS" || categoryName === categoryFilter;

    const matchesUrgency =
      urgencyFilter === "TODOS" ||
      (urgencyFilter === "COM_URGENCIA" && product?.hasUrgency) ||
      (urgencyFilter === "SEM_URGENCIA" && !product?.hasUrgency);

    return matchesSearch && matchesCategory && matchesUrgency;
  });

  // if (!company?.id) {
  //   return (
  //     <EmptyState
  //       title="Finalize seu cadastro"
  //       description="Complete os dados da empresa para cadastrar e gerenciar produtos."
  //       actionLabel="Finalizar cadastro"
  //       onAction={() => window.location.assign("/empresa")}
  //     />
  //   );
  // }

  // Paginação

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  return (
    <div className="fade-in page-stack">
      <div className="page-header">
        <div>
          <h2>Produtos</h2>
          <p>Lista de produtos cadastrados pela sua empresa.</p>
          {isVerificationBlocked ? (
            <p className="muted" style={{ marginTop: "0.35rem" }}>
              Para cadastrar produtos, sua empresa precisa estar com cadastro
              aprovado.
            </p>
          ) : null}
        </div>
        {canCreateProduct ? (
          <Link className="button button--primary" to="/produtos/novo">
            Novo Produto
          </Link>
        ) : (
          <button
            className="button button--ghost"
            type="button"
            disabled
            aria-disabled="true"
            style={{ cursor: "not-allowed", opacity: 0.7 }}
          >
            Cadastro em análise
          </button>
        )}
      </div>

      <div className="section-card">
        <h3 className="card-title">Produtos cadastrados</h3>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "12px",
            alignItems: "end",
            marginBottom: "16px",
          }}
        >
          <div className="field" style={{ margin: 0 }}>
            <label className="label">Buscar produto</label>
            <input
              className="input"
              type="text"
              placeholder="Nome, categoria ou condição"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>

          <div className="field" style={{ margin: 0 }}>
            <label className="label">Categoria</label>
            <select
              className="select"
              value={categoryFilter}
              onChange={(event) => setCategoryFilter(event.target.value)}
            >
              <option value="TODAS">Todas</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <div className="field" style={{ margin: 0 }}>
            <label className="label">Urgência</label>
            <select
              className="select"
              value={urgencyFilter}
              onChange={(event) => setUrgencyFilter(event.target.value)}
            >
              <option value="TODOS">Todos</option>
              <option value="COM_URGENCIA">Com alerta</option>
              <option value="SEM_URGENCIA">Sem alerta</option>
            </select>
          </div>

          <button
            className="button button--ghost"
            type="button"
            onClick={() => {
              setSearchTerm("");
              setCategoryFilter("TODAS");
              setUrgencyFilter("TODOS");
            }}
            style={{ height: "44px", width: "100%" }}
          >
            Limpar
          </button>
        </div>

        {loading ? (
          <div className="muted">Carregando produtos...</div>
        ) : products.length === 0 ? (
          <NoData
            title="Nenhum produto cadastrado"
            description={
              canCreateProduct
                ? "Você ainda não cadastrou produtos. Adicione seu primeiro item ao seu catálogo."
                : "Sua empresa ainda não está aprovada para publicar produtos."
            }
          >
            <button
              className="button button--primary"
              style={{ marginTop: "1.2rem" }}
              onClick={() => navigate(canCreateProduct ? "/produtos/novo" : "/empresa")}
            >
              {canCreateProduct ? "Adicionar Produto" : "Ir para Minha Empresa"}
            </button>
          </NoData>
        ) : filteredProducts.length === 0 ? (
          <NoData title="Nenhum resultado para os filtros" description="Tente ajustar os filtros para encontrar produtos." />
        ) : (
          <>
          <table className="table">
            <thead>
              <tr>
                <th>Produto / Lote</th>
                <th>Categoria</th>
                <th>Condição</th>
                <th>Validade Próxima</th>
                <th>Atualizado</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => {
                const categoryName =
                  product?.Category?.name ||
                  product?.category?.name ||
                  product?.categoryName ||
                  "-";
                const price = product?.priceCondition || "-";
                const urgencyBadge = product?.hasUrgency ? (
                  <span
                    style={{
                      backgroundColor: "#ffedd5",
                      color: "#ea580c",
                      padding: "0.25rem 0.5rem",
                      borderRadius: "9999px",
                      fontSize: "0.85rem",
                      fontWeight: "600",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Laranja Alerta
                  </span>
                ) : (
                  <span className="muted">-</span>
                );
                const updatedAt = formatDate(
                  product?.updatedAt || product?.createdAt,
                );

                return (
                  <tr key={product.id}>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.75rem",
                        }}
                      >
                        {getProductPreviewSrc(product) && (
                          <img
                            src={getProductPreviewSrc(product)}
                            alt={product.title}
                            style={{
                              width: "40px",
                              height: "40px",
                              objectFit: "cover",
                              borderRadius: "0.25rem",
                              border: "1px solid var(--color-border)",
                            }}
                          />
                        )}
                        <strong>{product?.title || "-"}</strong>
                      </div>
                    </td>
                    <td>{categoryName}</td>
                    <td>{price}</td>
                    <td>{urgencyBadge}</td>
                    <td>{updatedAt}</td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        <button
                          className="button button--ghost"
                          type="button"
                          onClick={() => handleEditProduct(product.id)}
                          aria-label={`Editar produto ${product?.title || ""}`}
                          title="Editar produto"
                          style={{
                            width: "40px",
                            height: "40px",
                            padding: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <FiEdit2 size={16} />
                        </button>
                        <button
                          className="button button--ghost"
                          type="button"
                          onClick={() => handleDeleteProduct(product.id)}
                          aria-label={`Excluir produto ${product?.title || ""}`}
                          title="Excluir produto"
                          disabled={activeProductId === product.id}
                          style={{
                            width: "40px",
                            height: "40px",
                            padding: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#b91c1c",
                          }}
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* Paginação bonita */}
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
          </>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
